import { PreviewControls } from '@hendrx/core/common/components/preview/PreviewControls';
import { LearningActivityPreviewProviders } from '@hendrx/core/learningActivity/components/LearningActivityPreviewProviders';
import { LearningActivityPreviewPage } from '@hendrx/core/learningActivity/pages/LearningActivityPreviewPage';
import { useGetLearningActivityDraftByIdQuery } from '@hendrx/core/learningActivity/queries/getLearningActivityDraftById';
import { Center, Container } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useLearningObjectCommonData } from 'shared/components/providers/LearningObjectCommonDataProvider';
import { DeviceType } from 'shared/types/DeviceType';
import { useCustomUIConfigurations } from 'shared/utils/useCustomUIConfigurations';

export const Route = createFileRoute('/studio/learning-activity/preview/$learningActivityDraftId')({
  component: RouteComponent,
  validateSearch: z.object({
    device: z.string().optional()
  })
});

function RouteComponent() {
  const { learningActivityDraftId } = Route.useParams();
  return (
    <LearningActivityPreviewProviders learningActivityDraftId={learningActivityDraftId}>
      <PreviewPage />
    </LearningActivityPreviewProviders>
  );
}

function PreviewPage() {
  const { t } = useTranslation('components');
  const { learningObjectDraftId: learningActivityDraftId } = useLearningObjectCommonData();
  const { isLoading, error } = useGetLearningActivityDraftByIdQuery(learningActivityDraftId);

  const navigate = useNavigate();
  const { height } = useViewportSize();

  const handleOnSelectDevice = useCallback(
    (device: DeviceType) => {
      void navigate({ from: Route.fullPath, search: prev => ({ ...prev, device }) });
    },
    [navigate]
  );

  const { disableDevicePicker } = useCustomUIConfigurations();

  if (error) {
    return (
      <Container h={height} size="100%">
        <Center h="100%">{t('studio.learningObjectDraftFetchError')}</Center>
      </Container>
    );
  }

  return (
    <>
      {!isLoading && (
        <>
          <LearningActivityPreviewPage />
          {!disableDevicePicker && (
            <PreviewControls withDeviceSelector onSelectDevice={handleOnSelectDevice} position="left" />
          )}
        </>
      )}
    </>
  );
}
